import { useCallback, useEffect, useRef, useState } from 'react'

import axios from 'axios'

import {
  AudioFileUploadFileInfo,
  AudioFileUploadStatus,
} from 'src/Components/AudioFileUpload'
import {
  AUDIO_FILE_ERROR_MESSAGES,
  AudioFileErrorCode,
} from 'src/Components/AudioFileUpload/validation'
import { useAppDispatch } from 'src/Hooks/redux'
import {
  SpotifyCampaignTrackFile,
  spotifyCampaignStatusApiUtil,
} from 'src/Redux/spotifyCampaignStatusApi'
import {
  PreReleaseTrackFileUploadProgress,
  deletePreReleaseTrackFile,
  isUploadTimeoutError,
  uploadPreReleaseTrackFile,
} from 'src/Services/preReleaseTrackFile'

import { reportTrackFileUploadFailure } from './reportTrackFileUploadFailure'

export const LEAVE_DURING_UPLOAD_CONFIRMATION =
  'Your track is still uploading. Leave and cancel the upload?'

export interface UseTrackFileUploadParams {
  campaignId: string
  savedFile: SpotifyCampaignTrackFile | null
  onSaved: () => void
}

export interface UseTrackFileUploadResult {
  file: AudioFileUploadFileInfo | null
  status: AudioFileUploadStatus
  progress: number
  uploadProgress: PreReleaseTrackFileUploadProgress | null
  isProcessing: boolean
  error: string | null
  isSaving: boolean
  canSave: boolean
  selectFile: (file: File) => void
  removeFile: () => void
  saveFile: () => void
  reportValidationError: (code: AudioFileErrorCode, file: File) => void
  confirmLeave: () => boolean
}

const toFileInfo = (
  savedFile: SpotifyCampaignTrackFile | null,
): AudioFileUploadFileInfo | null => {
  if (!savedFile) {
    return null
  }
  return {
    name: savedFile.name,
    ...(savedFile.sizeBytes === null ? {} : { size: savedFile.sizeBytes }),
  }
}

const getUploadErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError<{ reason?: string }>(error)) {
    return error.response?.data.reason || AUDIO_FILE_ERROR_MESSAGES.uploadFailed
  }
  return AUDIO_FILE_ERROR_MESSAGES.uploadFailed
}

const toPercentage = ({
  loaded,
  total,
}: PreReleaseTrackFileUploadProgress): number =>
  total > 0 ? Math.min(100, (loaded / total) * 100) : 0

const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
  event.preventDefault()
}

export const useTrackFileUpload = ({
  campaignId,
  savedFile,
  onSaved,
}: UseTrackFileUploadParams): UseTrackFileUploadResult => {
  const dispatch = useAppDispatch()
  const [file, setFile] = useState<AudioFileUploadFileInfo | null>(() =>
    toFileInfo(savedFile),
  )
  const [status, setStatus] = useState<AudioFileUploadStatus>(() =>
    savedFile ? 'uploaded' : 'idle',
  )
  const [uploadProgress, setUploadProgress] =
    useState<PreReleaseTrackFileUploadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [isSaving, setIsSaving] = useState(false)
  const [hasRemovedFile, setHasRemovedFile] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const hasStoredFileRef = useRef(Boolean(savedFile))
  const uploadIdRef = useRef(0)
  const uploadAbortControllerRef = useRef<AbortController | null>(null)

  const refreshCampaign = useCallback(() => {
    dispatch(
      spotifyCampaignStatusApiUtil.invalidateTags(['SpotifyCampaignDetails']),
    )
  }, [dispatch])

  const clearCachedTrackFile = useCallback(() => {
    dispatch(
      spotifyCampaignStatusApiUtil.updateQueryData(
        'getSpotifyCampaignStatus',
        Number(campaignId),
        (draft) => {
          draft.track.audioUrl = null
        },
      ),
    )
  }, [campaignId, dispatch])

  const selectFile = useCallback(
    (selectedFile: File) => {
      uploadIdRef.current += 1
      const uploadId = uploadIdRef.current
      const abortController = new AbortController()
      uploadAbortControllerRef.current = abortController
      setHasUserInteracted(true)
      setError(null)
      setUploadProgress(null)
      setFile({ name: selectedFile.name, size: selectedFile.size })
      setStatus('uploading')

      const run = async (): Promise<void> => {
        if (hasStoredFileRef.current || savedFile) {
          await deletePreReleaseTrackFile(campaignId)
          hasStoredFileRef.current = false
          setHasRemovedFile(true)
          clearCachedTrackFile()
        }
        await uploadPreReleaseTrackFile({
          campaignId,
          file: selectedFile,
          signal: abortController.signal,
          onProgress: (nextProgress) => {
            if (uploadId === uploadIdRef.current) {
              setUploadProgress(nextProgress)
            }
          },
        })

        if (uploadId !== uploadIdRef.current) {
          await deletePreReleaseTrackFile(campaignId)
          setHasRemovedFile(true)
          refreshCampaign()
          return
        }
        hasStoredFileRef.current = true
        setStatus('uploaded')
        refreshCampaign()
      }

      void run().catch((uploadError: unknown) => {
        if (uploadId !== uploadIdRef.current) {
          return
        }
        reportTrackFileUploadFailure({
          campaignId,
          reason: isUploadTimeoutError(uploadError)
            ? 'timeout'
            : 'uploadFailed',
          file: selectedFile,
        })
        setFile(null)
        setStatus('idle')
        setUploadProgress(null)
        setError(getUploadErrorMessage(uploadError))
      })
    },
    [campaignId, clearCachedTrackFile, refreshCampaign, savedFile],
  )

  const removeFile = useCallback(() => {
    uploadIdRef.current += 1
    setHasUserInteracted(true)
    setFile(null)
    setStatus('idle')
    setUploadProgress(null)
    setError(null)

    if (!hasStoredFileRef.current && !savedFile) {
      return
    }
    hasStoredFileRef.current = false
    clearCachedTrackFile()
    void deletePreReleaseTrackFile(campaignId)
      .then(() => {
        setHasRemovedFile(true)
        refreshCampaign()
      })
      .catch(() => {
        setError(AUDIO_FILE_ERROR_MESSAGES.uploadFailed)
        refreshCampaign()
      })
  }, [campaignId, clearCachedTrackFile, refreshCampaign, savedFile])

  const reportValidationError = useCallback(
    (code: AudioFileErrorCode, invalidFile: File) => {
      reportTrackFileUploadFailure({
        campaignId,
        reason: code,
        file: invalidFile,
      })
    },
    [campaignId],
  )

  const currentFile = hasUserInteracted ? file : toFileInfo(savedFile)
  let currentStatus: AudioFileUploadStatus
  if (hasUserInteracted) {
    currentStatus = status
  } else if (savedFile) {
    currentStatus = 'uploaded'
  } else {
    currentStatus = 'idle'
  }

  const isUploading = currentStatus === 'uploading'
  const isProcessing =
    isUploading &&
    uploadProgress !== null &&
    uploadProgress.loaded >= uploadProgress.total

  useEffect(() => {
    if (isUploading) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isUploading])

  const confirmLeave = useCallback(() => {
    if (!isUploading) {
      return true
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(LEAVE_DURING_UPLOAD_CONFIRMATION)) {
      return false
    }
    uploadIdRef.current += 1
    if (!isProcessing) {
      uploadAbortControllerRef.current?.abort()
    }
    return true
  }, [isProcessing, isUploading])

  const canSave =
    currentStatus === 'uploaded' || (currentStatus === 'idle' && hasRemovedFile)

  const saveFile = useCallback(() => {
    if (!canSave) {
      return
    }
    setIsSaving(true)
    refreshCampaign()
    onSaved()
  }, [canSave, onSaved, refreshCampaign])

  return {
    file: currentFile,
    status: currentStatus,
    progress: uploadProgress ? toPercentage(uploadProgress) : 0,
    uploadProgress: isUploading ? uploadProgress : null,
    isProcessing,
    error,
    isSaving,
    selectFile,
    removeFile,
    saveFile,
    reportValidationError,
    confirmLeave,
    canSave,
  }
}
